import React, { useState, useEffect, useRef } from "react";
import useDarkMode from '../../../../hooks/useDarkMode';
import useThemeColor from "../../../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import CustomModal from "../customModal";


function AllergenSelectionModal({
  showModal,
  setShowModal,
  onSubmit,
  selectedAllergens,
  setSelectedAllergens,
  allergens,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredAllergens, setFilteredAllergens] = useState([]);
  const [localSelectedAllergens, setLocalSelectedAllergens] = useState([]);
  const isDarkMode = useDarkMode();
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  // Safeguard: Ensure selectedAllergens is always an array
  const safeSelectedAllergens = Array.isArray(selectedAllergens) ? selectedAllergens : [];

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  

  // Sync localSelectedAllergens with selectedAllergens when modal opens
  useEffect(() => {
    if (showModal) {
      setLocalSelectedAllergens(safeSelectedAllergens);
      setFilteredAllergens(allergens); // Initialize filtered list
    }
  }, [showModal, safeSelectedAllergens, allergens]);

  // Filter allergens based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredAllergens(allergens);
    } else {
      const filtered = allergens.filter((allergen) =>
        allergen.allergen_Name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
      );
      setFilteredAllergens(filtered);
    }
  }, [searchTerm, allergens]);

  const handleSubmit = () => {
    if (typeof onSubmit === "function") {
      setSelectedAllergens(localSelectedAllergens); // Update parent state
      onSubmit(localSelectedAllergens); // Pass to parent callback
    }
    setShowModal(false); // Close modal
    setSearchTerm("");
  };

  const handleClose = () => {
    setShowModal(false);
    setSearchTerm("");
    setLocalSelectedAllergens(safeSelectedAllergens);
  };

  const handleAllergenSelection = (allergen) => {
    setLocalSelectedAllergens((prev) => {
      const safePrev = Array.isArray(prev) ? prev : [];
      if (safePrev.some((a) => a.id === allergen.id)) {
        return safePrev.filter((a) => a.id !== allergen.id);
      }
      return [...safePrev, allergen];
    });
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
    e.currentTarget.style.cursor = "grabbing";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === overIndex ||
      draggedIndex === undefined ||
      overIndex === undefined
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const newAllergens = [...filteredAllergens];
    const draggedAllergen = newAllergens[draggedIndex];
    newAllergens.splice(draggedIndex, 1);
    newAllergens.splice(overIndex, 0, draggedAllergen);

    setFilteredAllergens(newAllergens);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    e.currentTarget.style.cursor = "grab";
    document.querySelectorAll(".allergen-card").forEach((card) => {
      card.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const RenderAllergenCard = ({ allergen, index }) => {
    const isSelected = localSelectedAllergens.some((a) => a.id === allergen.id);

    return (
      <div
        className="allergen-card"
        draggable
        onDragStart={(e) => handleDragStart(e, index)}
        onDragEnter={(e) => handleDragEnter(e, index)}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
        style={{
          background: isSelected 
            ? (isDarkMode ? `${themeColor}20` : `${themeColor}15`) 
            : (isDarkMode ? '#404040' : '#ffffff'),
          borderRadius: '12px',
          padding: '16px 20px',
          border: isSelected 
            ? `2px solid ${themeColor}` 
            : (isDarkMode ? '2px solid #555' : '2px solid #e9ecef'),
          transition: 'all 0.2s ease',
          cursor: 'grab',
          position: 'relative',
          boxShadow: isSelected 
            ? `0 4px 12px ${themeColor}25` 
            : (isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'),
          marginBottom: '12px'
        }}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = themeColor;
            e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}25`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
            e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
        onClick={() => handleAllergenSelection(allergen)}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            {/* Drag Handle */}
            <div
              style={{
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                cursor: 'grab',
                color: isDarkMode ? '#ffffff' : '#6c757d',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode ? '#555' : '#f8f9fa';
                e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#495057';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#6c757d';
              }}
            >
              <i className="fa fa-bars" style={{ fontSize: '12px' }}></i>
            </div>
            {/* Allergen Icon */}
            <div
              style={{
                width: '36px',
                height: '36px',
                background: isSelected ? themeColor : (isDarkMode ? '#555' : '#f8f9fa'),
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '12px',
                border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef',
                transition: 'all 0.2s ease'
              }}
            >
              <i
                className="fa fa-exclamation-triangle"
                style={{
                  fontSize: '14px',
                  color: isSelected ? 'white' : (isDarkMode ? '#ffffff' : '#6c757d')
                }}
              ></i>
            </div>
            {/* Allergen Info */}
            <div>
              <span
                style={{
                  fontSize: '15px',
                  fontWeight: '500',
                  color: isSelected ? themeColor : (isDarkMode ? '#ffffff' : '#495057')
                }}
              >
                {allergen.allergen_Name}
              </span>
              {allergen.allergen_Description && (
                <small
                  style={{
                    color: isSelected ? themeColor : (isDarkMode ? '#ffffff' : '#6c757d'),
                    fontSize: '11px',
                    lineHeight: '1.3',
                    display: 'block'
                  }}
                >
                  {allergen.allergen_Description}
                </small>
              )}
            </div>
          </div>
          {/* Custom Checkbox */}
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '6px',
              border: isSelected ? `2px solid ${themeColor}` : (isDarkMode ? '2px solid #666' : '2px solid #e9ecef'),
              background: isSelected ? themeColor : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleAllergenSelection(allergen);
            }}
          >
            {isSelected && (
              <i
                className="fa fa-check"
                style={{
                  fontSize: '12px',
                  color: 'white'
                }}
              ></i>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <CustomModal
      isOpen={showModal}
      title="Select Allergens"
      subtitle="Select allergens present in this item"
      onClose={handleClose}
      onSubmit={handleSubmit}
      showButton={true}
      submitButtonText="Select"
      isCentered={true}
      showSubmitButton={true}
      showCancelButton={true}
      modalSize="modal-lg"
      maxHeight="600px"
    >
      {/* Search Input */}
      <div className="mb-4">
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '16px',
              zIndex: 2,
              color: isDarkMode ? '#ffffff' : '#6c757d',
              fontSize: '16px'
            }}
          >
            <i className="fa fa-search"></i>
          </div>
          <input
            type="text"
            className="form-control"
            placeholder="Search allergens..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              paddingLeft: '48px',
              paddingRight: searchTerm ? '48px' : '16px',
              borderRadius: '12px',
              border: isDarkMode ? '2px solid #555' : '2px solid #e9ecef',
              fontSize: '15px',
              height: '48px',
              background: isDarkMode ? '#2d3436' : '#f8f9fa',
              color: isDarkMode ? '#ffffff' : '#000000',
              transition: 'all 0.2s ease'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = themeColor;
              e.target.style.background = isDarkMode ? '#404040' : '#ffffff';
              e.target.style.boxShadow = `0 0 0 3px ${themeColor}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
              e.target.style.background = isDarkMode ? '#2d3436' : '#f8f9fa';
              e.target.style.boxShadow = 'none';
            }}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              style={{
                position: 'absolute',
                right: '12px',
                background: 'transparent',
                border: 'none',
                color: isDarkMode ? '#ffffff' : '#6c757d',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode ? '#555' : '#e9ecef';
                e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#495057';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#6c757d';
              }}
            >
              <i className="fa fa-times" style={{ fontSize: '14px' }}></i>
            </button>
          )}
        </div>
      </div>
      {/* Allergen Cards */}
      <div style={{ height: "400px", overflowY: "auto", paddingRight: '8px' }}>
        {filteredAllergens && filteredAllergens.length > 0 ? (
          filteredAllergens.map((allergen, index) => (
            <RenderAllergenCard key={allergen.id} allergen={allergen} index={index} />
          ))
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: isDarkMode ? '#ffffff' : '#6c757d'
            }}
          >
            <div
              style={{
                width: '80px',
                height: '80px',
                background: isDarkMode ? '#555' : '#f8f9fa',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
              }}
            >
              <i className="fa fa-exclamation-triangle" style={{ fontSize: '32px', color: isDarkMode ? '#ffffff' : '#adb5bd' }}></i>
            </div>
            <h5 style={{ color: isDarkMode ? '#ffffff' : '#495057', marginBottom: '8px' }}>
              No allergens found
            </h5>
            <p style={{ margin: 0, color: isDarkMode ? '#ffffff' : '#6c757d' }}>
              {searchTerm.length > 0
                ? `No results matching "${searchTerm}"`
                : "No allergens available"}
            </p>
          </div>
        )}
      </div>
    </CustomModal>
  );
}

export default AllergenSelectionModal;